import { DateTime } from 'luxon';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, OnDestroy, DOCUMENT } from '@angular/core';
import {
  CanActivate,
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router';
import { OAuthEvent, OAuthService } from 'angular-oauth2-oidc';
import {
  BehaviorSubject,
  Observable,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  first,
  forkJoin,
  map,
  of,
  retry,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { Title } from '@angular/platform-browser';

import { environment } from 'src/environments/environment';

import { Mandant, MandantenService } from '../../mandanten.service';

import { version } from './../../../../../environments/version';

const API_ENDPOINT = `${environment.BACKEND_URL}/user`;
const API_VERSION_ENDPOINT = `${environment.BACKEND_VERSION}`;

export interface IUser {
  email: string;
  oid: string;
  givenName: string;
  familyName: string;
  vorname?: string;
  nachname?: string;
  _mandant_id?: number;
  _id?: number;
}

export interface IVersion {
  version: string;
  uptime: string;
}

const defaultPath = '/';

/**
 *
 */
@Injectable()
export class AuthService implements OnDestroy {
  private readonly storageListenerUnlisten: () => void;
  private startUpUrl = '';
private _userInfo$ = new BehaviorSubject<IUser | null>(null);
  private _hasValidToken$ = new BehaviorSubject<boolean>(
    false
  );
  private _hasUserInfo$ = new BehaviorSubject<boolean>(false);
  private _hasMandantSelected$ = new BehaviorSubject<boolean>(
    false
  );
  private _selectedMandantId$ = new BehaviorSubject<number>(
    this.getInitialMandantId()
  );
private _isAuthenticated$ = new BehaviorSubject<boolean>(
    false
  );
private _isLocked$ = new BehaviorSubject<boolean>(
    false
  );
private _Mandanten$: BehaviorSubject<Mandant[] | null> =
    new BehaviorSubject<Mandant[] | null>(null);
private _initialRequestedUrl: string = '';
private _backendVersion$ = new BehaviorSubject<IVersion | null>(null);
public readonly selectedMandant$:Observable<Mandant> = combineLatest( {selectedMandant: this._selectedMandantId$.pipe(filter( x => Boolean(x))), mandanten: this._Mandanten$}).pipe( map( ({selectedMandant, mandanten}) => {  
  if (selectedMandant && mandanten) {
    return mandanten.find(mandant => mandant._id === selectedMandant);
  }
  return null;
}), filter( mandant => mandant !== null && mandant !== undefined), distinctUntilChanged());


  /**
   *
   * @param router
   * @param oAuthService
   * @param httpClient
   * @param mandantService
   * @param titleService
   * @param document
   */
  constructor(
    private readonly router: Router,
    private readonly oAuthService: OAuthService,
    private readonly httpClient: HttpClient,
    private readonly mandantService: MandantenService,
    private readonly titleService: Title,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    // Register for Events
    this.oAuthService.events
      .pipe(
        tap((event: OAuthEvent) => {
          if (['session_terminated', 'session_error'].includes(event.type)) {
            this._hasValidToken$.next(false);
          }

          if (['token_refreshed', 'token_received'].includes(event.type)) {
            this._hasValidToken$.next(this.oAuthService.hasValidAccessToken());
          }

          if (['token_expires'].includes(event.type)) {
            //this.login();
          }
        })
      )
      .subscribe();

    this._hasValidToken$
      .asObservable()
      .pipe(
        tap((x) => console.log('Received change', x)),
        distinctUntilChanged(),
        tap((hasValidToken) => {
          // Reset User Info if no token is here
          if (hasValidToken === false) {
            this._hasUserInfo$.next(false);            
          }
          this._isLocked$.next(false);
        }),
        filter((hasValidToken) => hasValidToken === true),
        tap(() => console.log('Try to load user Info')),
        switchMap(() => {
          console.log('SWMap');
          return forkJoin({
            user: this.httpClient
              .get<IUser>(`${API_ENDPOINT}/`)
              .pipe(
                catchError((error: unknown) => {
                  // Handle 401 immediately without retrying
                  if (error instanceof HttpErrorResponse && error.status === 401) {
                    this._isLocked$.next(true);
                    return of(null);
                  }
                  // For other errors, re-throw to allow retry
                  return throwError(() => error);
                }),
                retry({ count: 8, delay: 3000 })
              ),
            version: this.httpClient
              .get<IVersion>(`${API_VERSION_ENDPOINT}/`)
              .pipe(retry({ count: 8, delay: 3000 })),
            mandanten: this.mandantService.Store$.pipe(first()),
          });
        }),
        tap((x) => {
          

          if (!x.user) {
            this._isAuthenticated$.next(false);
            this._isLocked$.next(true);
            return;
          }
          this._userInfo$.next(x.user);
          this._hasUserInfo$.next(x.user && x.user.oid ? true : false);
          this._Mandanten$.next(x.mandanten);
          this._backendVersion$.next(x.version);

          const initialMandantId = this.getInitialMandantId();
          if (
            initialMandantId &&
            x.mandanten.findIndex(
              (mandant) => mandant._id === initialMandantId
            ) > -1
          ) {
            this._selectedMandantId$.next(initialMandantId);
          } else {
            if (x.user._mandant_id) {
              this._selectedMandantId$.next(x.user._mandant_id);
            } else {
              this._selectedMandantId$.next(0);
            }
          }

          if (initialMandantId > 0) {
            if (this.InitialRequestedUrl) {
              this.router.navigateByUrl(this.InitialRequestedUrl);
              this.InitialRequestedUrl = '';
              return;
            }
          }
          this.router.navigate(['home']);
        })
      )
      .subscribe();

    combineLatest([this._hasValidToken$, this._hasUserInfo$])
      .pipe(
        tap(([hasValidToken, hasUserInfo]) => {
          

          this._isAuthenticated$.next(hasValidToken && hasUserInfo);
        })
      )
      .subscribe();

    this._selectedMandantId$
      .asObservable()
      .pipe(
        tap((mandantId) => {
          
          sessionStorage.setItem('mandantId', mandantId.toString());
          this.mandantService.currentMandantId = mandantId;
        })
      )
      .subscribe();

    this.storageListenerUnlisten = () => {};
  }

  /**
   *
   */
  public get InitialRequestedUrl(): string {
    return this._initialRequestedUrl;
  }

  /**
   *
   */
  public get SelectedMandant$(): Observable<Mandant | null> {
    return combineLatest([
      this._Mandanten$.asObservable(),
      this._selectedMandantId$,
    ]).pipe(
      map(([mandanten, mandantId]) => {
        if (mandanten && mandanten.length > 0 && mandantId && mandantId > 0) {
          return mandanten.find((mandant) => mandant._id === mandantId) || null;
        }

        return null;
      })
    );
  }

  /**
   *
   */
  public get isAuthenticated(): boolean {
    return this._isAuthenticated$.getValue();
  }

  /**
   *
   */
  public get MandantId(): number {
    return this._selectedMandantId$.getValue();
  }

  /**
   *
   */
  public get isAuthenticated$(): Observable<boolean> {
    return this._isAuthenticated$.asObservable();
  }

  /**
   *
   */
  public get isLocked$(): Observable<boolean> {
    return this._isLocked$.asObservable();
  }

  /**
   *
   */
  public get isMandantSelected$(): Observable<boolean> {
    return combineLatest([
      this.isAuthenticated$,
      this._selectedMandantId$.asObservable(),
    ]).pipe(map((val) => val[0] === true && val[1] > 0));
  }

  /**
   *
   */
  public get userInfo$(): Observable<IUser | null> {
    return this._userInfo$.asObservable();
  }

  /**
   *
   */
  public get userInfo(): IUser | null {
    return { ...this._userInfo$.getValue() } as IUser | null;
  }

  /**
   *
   */
  public set InitialRequestedUrl(uri: string) {
    console.log('set uri to', uri);
    this._initialRequestedUrl = uri;
  }

  /**
   *
   */
  public set MandantId(id: number) {
    this._selectedMandantId$.next(id);
  }

  /**
   *
   */
  public get backendVersion(): null | IVersion {
    const backendVersion = this._backendVersion$.getValue();
    if (!backendVersion) {
      return null;
    }

    return {
      version: backendVersion.version,
      uptime: DateTime.fromSeconds(Math.abs(+backendVersion.uptime)).toFormat(
        'HH:mm:ss'
      ),
    };
  }

  /**
   *
   */
  ngOnDestroy(): void {
    this.storageListenerUnlisten();
  }

  /**
   *
   */
  public runInitialLoginSequence(): Promise<void> {
    if (location.pathname !== '/') {
      this.InitialRequestedUrl = location.pathname;
    }

    this.oAuthService.setupAutomaticSilentRefresh();

    return (
      this.oAuthService
        .loadDiscoveryDocument()
        // 1. HASH LOGIN:
        // Try to log in via hash fragment after redirect back
        // from IdServer from initImplicitFlow:
        .then(() => this.oAuthService.tryLogin())

        .then(() => {
          this._hasValidToken$.next(this.oAuthService.hasValidAccessToken());
          if (this.oAuthService.hasValidAccessToken()) {
            return Promise.resolve();
          }

          this.login();
          return Promise.resolve();
        })

        .then(() => {
          // Check for the strings 'undefined' and 'null' just to be sure. Our current
          // login(...) should never have this, but in case someone ever calls
          // initImplicitFlow(undefined | null) this could happen.
          if (
            this.oAuthService.state &&
            this.oAuthService.state !== 'undefined' &&
            this.oAuthService.state !== 'null'
          ) {
            let stateUrl = this.oAuthService.state;
            if (stateUrl.startsWith('/') === false) {
              stateUrl = decodeURIComponent(stateUrl);
            }
            console.log(
              `There was state of ${this.oAuthService.state}, so we are sending you to: ${stateUrl}`
            );
            this.router.navigateByUrl(stateUrl);
          }

          this._hasValidToken$.next(this.oAuthService.hasValidAccessToken());
        })
        .catch(() => console.log('Some Error occured'))
    );
  }

  /**
   *
   * @param targetUrl
   */
  public login(targetUrl?: string) {
    // Note: before version 9.1.0 of the library you needed to
    // call encodeURIComponent on the argument to the method.

    
    this.oAuthService.initLoginFlow(targetUrl || this.router.url);
  }

  /**
   *
   */
  public logout() {
    this.MandantId = 0;
    this.oAuthService.logOut();
  }

  /**
   *
   */
  private getInitialMandantId(): number {
    const mandantId = parseInt(sessionStorage.getItem('mandantId') || '', 10);

    // Check if mandantId is a number (since getItem can return null if the key does not exist)
    if (!isNaN(mandantId)) {
      return mandantId;
    } else {
      return 0;
    }
  }
}

/**
 *
 */
@Injectable()
export class AuthGuard implements CanActivate {
  /**
   *
   * @param router
   * @param authService
   */
  constructor(
    private readonly router: Router,
    private readonly authService: AuthService
  ) {}

  /**
   *
   * @param route
   * @param state
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean {
    const isLoggedIn = this.authService.isAuthenticated;
    const isAuthForm = ['mandant'].includes(
      route.routeConfig?.path || defaultPath
    );

    console.log('State Url', state.url);
    if (!isLoggedIn) {
      this.authService.InitialRequestedUrl = state.url;
    }

    console.log('Is Logged in', isLoggedIn, route.routeConfig?.path);
    if (isLoggedIn && this.authService.MandantId < 1) {
      if (isAuthForm === false) {
        this.router.navigate(['mandant']);
        return false;
      }
      return true;
    }

    return isLoggedIn;
  }
}
